import { type RechartsChartOffset as ChartOffset, type RechartsNumberDomain as NumberDomain, type RechartsPlotArea as PlotArea, RechartsReferenceArea as ReferenceArea } from '@precis-digital/kurama/recharts';
import { type ReactElement } from 'react';
import type { CartesianAxisType, ChartCategoricalDomain as CategoricalDomain, DimensionAxisType, DimensionDomain, DimensionOrMetricDomain, MetricDomain } from '../internalTypes';
import { assertNever } from './generics';
import type { ChartCardDefaults, LayoutType } from '../types';
interface GetFirstAndSecondDomainsProps {
  firstAxisCategoricalDomain: CategoricalDomain | undefined;
  xSingle: boolean;
  ySingle: boolean;
  xDomain: CategoricalDomain | NumberDomain;
  yDomain: CategoricalDomain | NumberDomain;
}
interface GetFirstAndSecondDomainsResult {
  firstDomain: DimensionOrMetricDomain;
  secondDomain: MetricDomain;
}
export const getFirstAndSecondDomainsHorizontal = ({
  firstAxisCategoricalDomain,
  xSingle,
  ySingle,
  xDomain,
  yDomain
}: GetFirstAndSecondDomainsProps): GetFirstAndSecondDomainsResult => {
  let firstDomain: DimensionOrMetricDomain;
  if (firstAxisCategoricalDomain == null) {
    firstDomain = xSingle ? undefined : xDomain as NumberDomain;
  } else {
    firstDomain = xSingle ? undefined : firstAxisCategoricalDomain.slice(firstAxisCategoricalDomain.indexOf(xDomain[0] as string), firstAxisCategoricalDomain.indexOf(xDomain[1] as string) + 1);
  }
  const secondDomain = ySingle ? undefined : yDomain as NumberDomain;
  return {
    firstDomain,
    secondDomain
  };
};
export const getFirstAndSecondDomainsVertical = ({
  firstAxisCategoricalDomain,
  xSingle,
  ySingle,
  xDomain,
  yDomain
}: GetFirstAndSecondDomainsProps): GetFirstAndSecondDomainsResult => {
  let firstDomain: DimensionOrMetricDomain;
  if (firstAxisCategoricalDomain == null) {
    firstDomain = ySingle ? undefined : yDomain as NumberDomain;
  } else {
    firstDomain = ySingle ? undefined : firstAxisCategoricalDomain.slice(firstAxisCategoricalDomain.indexOf(yDomain[0] as string), firstAxisCategoricalDomain.indexOf(yDomain[1] as string) + 1);
  }
  const secondDomain = xSingle ? undefined : xDomain as NumberDomain;
  return {
    firstDomain,
    secondDomain
  };
};
export interface Area {
  xDomain: CategoricalDomain | NumberDomain;
  xStart: number;
  xReverse: boolean;
  yDomain: CategoricalDomain | NumberDomain;
  yStart: number;
  yReverse: boolean;
  otherDomain: NumberDomain | null;
  singleAxis: CartesianAxisType | undefined;
}
const handleX1EqualX2 = ({
  x1,
  x2,
  firstAxisCategoricalDomain,
  firstAxisNumberDomain,
  secondAxisDomain,
  isHorizontal
}: {
  x1: string | number;
  x2: string | number;
  firstAxisCategoricalDomain: DimensionDomain;
  firstAxisNumberDomain: MetricDomain;
  secondAxisDomain: MetricDomain;
  isHorizontal: boolean;
}): {
  x1: string | number;
  x2: string | number;
} => {
  if (isHorizontal) {
    if (firstAxisCategoricalDomain != null) {
      x1 = firstAxisCategoricalDomain[0];
      x2 = firstAxisCategoricalDomain.at(-1) as string | number;
    }
    if (firstAxisNumberDomain != null) {
      x1 = firstAxisNumberDomain[0];
      x2 = firstAxisNumberDomain[1];
    }
  } else if (secondAxisDomain != null) {
    x1 = secondAxisDomain[0];
    x2 = secondAxisDomain[1];
  }
  return {
    x1,
    x2
  };
};
const handleY1EqualY2 = ({
  y1,
  y2,
  firstAxisCategoricalDomain,
  firstAxisNumberDomain,
  secondAxisDomain,
  isHorizontal
}: {
  y1: string | number;
  y2: string | number;
  firstAxisCategoricalDomain: DimensionDomain;
  firstAxisNumberDomain: MetricDomain;
  secondAxisDomain: MetricDomain;
  isHorizontal: boolean;
}): {
  y1: string | number;
  y2: string | number;
} => {
  if (isHorizontal) {
    if (secondAxisDomain != null) {
      y1 = secondAxisDomain[0];
      y2 = secondAxisDomain[1];
    }
  } else {
    if (firstAxisCategoricalDomain != null) {
      y1 = firstAxisCategoricalDomain[0];
      y2 = firstAxisCategoricalDomain.at(-1) as string | number;
    }
    if (firstAxisNumberDomain != null) {
      y1 = firstAxisNumberDomain[0];
      y2 = firstAxisNumberDomain[1];
    }
  }
  return {
    y1,
    y2
  };
};
export const getReferenceElement = ({
  defaults,
  xAxisId,
  yAxisId,
  refArea,
  isHorizontal,
  firstAxisCategoricalDomain,
  firstAxisNumberDomain,
  secondAxisDomain
}: {
  defaults: ChartCardDefaults;
  xAxisId: string;
  yAxisId: string;
  refArea: Area | null;
  isHorizontal: boolean;
  firstAxisCategoricalDomain: DimensionDomain;
  firstAxisNumberDomain: MetricDomain;
  secondAxisDomain: MetricDomain;
}): ReactElement | null => {
  if (refArea == null) return null;
  const {
    xDomain,
    yDomain,
    xReverse,
    yReverse
  } = refArea;
  let [x1, x2] = xReverse ? xDomain.toReversed() : xDomain;
  let [y1, y2] = yReverse ? yDomain.toReversed() : yDomain;
  if (x1 === x2 && y1 === y2) {
    return null;
  }
  if (x1 === x2) {
    ;
    ({
      x1,
      x2
    } = handleX1EqualX2({
      x1,
      x2,
      firstAxisCategoricalDomain,
      firstAxisNumberDomain,
      secondAxisDomain,
      isHorizontal
    }));
  }
  if (y1 === y2) {
    ;
    ({
      y1,
      y2
    } = handleY1EqualY2({
      y1,
      y2,
      firstAxisCategoricalDomain,
      firstAxisNumberDomain,
      secondAxisDomain,
      isHorizontal
    }));
  }
  return <ReferenceArea key="reference-area" xAxisId={xAxisId} x1={x1} x2={x2} yAxisId={yAxisId} y1={y1} y2={y2} stroke={defaults.zoomOverlay.stroke} strokeOpacity={defaults.zoomOverlay.strokeOpacity} strokeWidth={defaults.zoomOverlay.strokeWidth} fill={defaults.zoomOverlay.fill} fillOpacity={defaults.zoomOverlay.fillOpacity} pointerEvents="none" />;
};
const pixelToOverAxis = ({
  axis,
  yOverX,
  pixelX,
  pixelY,
  plotArea
}: {
  axis: CartesianAxisType;
  yOverX: boolean;
  pixelX: number;
  pixelY: number;
  plotArea: PlotArea;
}): boolean => {
  switch (axis) {
    case 'first':
      return yOverX ? pixelX < plotArea.x : pixelY >= plotArea.y + plotArea.height;
    case 'second':
      return yOverX ? pixelX >= plotArea.x + plotArea.width : pixelY >= plotArea.y + plotArea.height;
    case 'otherSecond':
      return yOverX ? pixelX < plotArea.x : pixelY < plotArea.y;
    default:
      assertNever(axis);
  }
  return false;
};
const pixelToNumericValue = ({
  axis,
  singleAxis,
  layout,
  plotArea,
  pixelX,
  pixelY,
  domain
}: {
  axis: CartesianAxisType;
  singleAxis?: CartesianAxisType;
  layout: LayoutType;
  plotArea: PlotArea | undefined;
  pixelX: number;
  pixelY: number;
  domain: MetricDomain;
}): {
  value: number | null;
  overAxis: boolean;
} => {
  if (plotArea == null || domain == null || !Array.isArray(domain) || singleAxis != null && singleAxis !== axis) {
    return {
      value: null,
      overAxis: false
    };
  }
  const yOverX = layout === 'horizontal' !== (axis === 'first');
  const plotValue = yOverX ? plotArea.y : plotArea.x;
  const plotSpace = yOverX ? plotArea.height : plotArea.width;
  const [minValue, maxValue] = domain as [number, number];

  // Adjust for plot area offset
  const relativeValue = (yOverX ? pixelY : pixelX) - plotValue;

  // Y-axis is inverted in SVG (0 is top)
  const ratio = relativeValue / plotSpace;
  const value = maxValue - ratio * (maxValue - minValue);
  const clampedValue = Math.max(minValue, Math.min(maxValue, value));
  return {
    value: clampedValue,
    overAxis: pixelToOverAxis({
      axis,
      yOverX,
      pixelX,
      pixelY,
      plotArea
    })
  };
};
const pixelToCategoricalValue = ({
  axis,
  singleAxis,
  layout,
  plotArea,
  domain,
  pixelX,
  pixelY
}: {
  axis: DimensionAxisType;
  singleAxis?: CartesianAxisType;
  layout: LayoutType;
  plotArea: PlotArea | undefined;
  domain: CategoricalDomain | undefined;
  pixelX: number;
  pixelY: number;
}): {
  value: string | null;
  overAxis: boolean;
} => {
  if (plotArea == null || domain == null || domain.length === 0 || singleAxis != null && singleAxis !== axis) {
    return {
      value: null,
      overAxis: false
    };
  }
  const yOverX = layout === 'vertical';
  const plotValue = yOverX ? plotArea.y : plotArea.x;
  const plotSpace = yOverX ? plotArea.height : plotArea.width;
  const relativeValue = (yOverX ? pixelY : pixelX) - plotValue;
  const categorySize = plotSpace / (domain.length - 1);
  const categoryIndex = Math.round(relativeValue / categorySize);
  const clampedIndex = Math.max(0, Math.min(categoryIndex, domain.length - 1));
  return {
    value: domain[clampedIndex],
    overAxis: pixelToOverAxis({
      axis: 'first',
      yOverX,
      pixelX,
      pixelY,
      plotArea
    })
  };
};
export const calculateZoomPointerEventValues = ({
  e,
  offset,
  plotArea,
  layout,
  isScatter,
  firstAxisNumberDomain,
  firstAxisCategoricalDomain,
  secondAxisDomain,
  otherSecondAxisDomain,
  singleAxis
}: {
  e: React.PointerEvent<SVGRectElement>;
  offset: ChartOffset | undefined;
  plotArea: PlotArea | undefined;
  layout: LayoutType;
  isScatter: boolean;
  firstAxisNumberDomain: MetricDomain;
  firstAxisCategoricalDomain: DimensionDomain;
  secondAxisDomain: MetricDomain;
  otherSecondAxisDomain: MetricDomain;
  singleAxis?: CartesianAxisType;
}): {
  pixelX: number;
  pixelY: number;
  firstAxisValue: number | string | null;
  secondAxisValue: number | null;
  otherSecondAxisValue: number | null;
  singleAxis: CartesianAxisType | undefined;
} => {
  const rect = e.currentTarget.getBoundingClientRect();
  const pixelX = e.clientX - rect.left + (plotArea?.x ?? 0) - (offset?.left ?? 0);
  const pixelY = e.clientY - rect.top + (plotArea?.y ?? 0) - (offset?.top ?? 0);
  const {
    value: firstAxisValue,
    overAxis: firstAxisOverAxis
  } = isScatter ? pixelToNumericValue({
    axis: 'first',
    singleAxis,
    layout,
    plotArea,
    pixelX,
    pixelY,
    domain: firstAxisNumberDomain
  }) : pixelToCategoricalValue({
    axis: 'first',
    singleAxis,
    layout,
    plotArea,
    domain: firstAxisCategoricalDomain,
    pixelX,
    pixelY
  });
  const {
    value: secondAxisValue,
    overAxis: secondAxisOverAxis
  } = pixelToNumericValue({
    axis: 'second',
    singleAxis,
    layout,
    plotArea,
    pixelX,
    pixelY,
    domain: secondAxisDomain
  });
  const {
    value: otherSecondAxisValue,
    overAxis: otherSecondAxisOverAxis
  } = pixelToNumericValue({
    axis: 'otherSecond',
    singleAxis,
    layout,
    plotArea,
    pixelX,
    pixelY,
    domain: otherSecondAxisDomain
  });
  if (e.type === 'pointerdown') {
    if (firstAxisOverAxis) {
      singleAxis = 'first';
    } else if (secondAxisOverAxis) {
      singleAxis = 'second';
    } else if (otherSecondAxisOverAxis) {
      singleAxis = 'otherSecond';
    } else {
      singleAxis = undefined;
    }
  }
  return {
    pixelX,
    pixelY,
    firstAxisValue,
    secondAxisValue,
    otherSecondAxisValue,
    singleAxis
  };
};
export const getXAndYStartAndDomainsOnStart = ({
  layout,
  pixelX,
  pixelY,
  firstAxisValue,
  secondAxisValue
}: {
  layout: LayoutType;
  pixelX: number;
  pixelY: number;
  firstAxisValue: number | string;
  secondAxisValue: number;
}): {
  xDomain: CategoricalDomain | NumberDomain;
  xStart: number;
  yDomain: CategoricalDomain | NumberDomain;
  yStart: number;
} => {
  if (layout === 'horizontal') {
    return {
      xDomain: [firstAxisValue, firstAxisValue] as CategoricalDomain | NumberDomain,
      xStart: pixelX,
      yDomain: [secondAxisValue, secondAxisValue] as CategoricalDomain | NumberDomain,
      yStart: pixelY
    };
  } else {
    return {
      xDomain: [secondAxisValue, secondAxisValue] as CategoricalDomain | NumberDomain,
      xStart: pixelY,
      yDomain: [firstAxisValue, firstAxisValue] as CategoricalDomain | NumberDomain,
      yStart: pixelX
    };
  }
};
interface GetXAndYDomainsAndReverseOnMoveProps {
  layout: LayoutType;
  xDomain: CategoricalDomain | NumberDomain;
  yDomain: CategoricalDomain | NumberDomain;
  pixelX: number;
  pixelY: number;
  xStart: number;
  yStart: number;
  xAxisMinChange: number;
  yAxisMinChange: number;
  firstAxisValue: number | string | null;
  secondAxisValue: number | null;
  singleAxis: CartesianAxisType | undefined;
}
interface GetXAndYDomainsAndReverseOnMoveResult {
  xDomain: CategoricalDomain | NumberDomain;
  xReverse: boolean;
  yDomain: CategoricalDomain | NumberDomain;
  yReverse: boolean;
}
const getXAndYDomainsAndReverseOnMoveHorizontal = ({
  xDomain,
  yDomain,
  pixelX,
  pixelY,
  xStart,
  yStart,
  yAxisMinChange,
  firstAxisValue,
  secondAxisValue,
  singleAxis
}: GetXAndYDomainsAndReverseOnMoveProps): GetXAndYDomainsAndReverseOnMoveResult => {
  if (firstAxisValue === xDomain[0]) {
    pixelX = xStart;
  }
  if (secondAxisValue != null && Math.abs(secondAxisValue - (yDomain[0] as number)) < yAxisMinChange) {
    secondAxisValue = yDomain[0] as number;
    pixelY = yStart;
  }
  return {
    xDomain: singleAxis != null && singleAxis !== 'first' ? xDomain : [xDomain[0], firstAxisValue] as CategoricalDomain | NumberDomain,
    xReverse: xStart > pixelX,
    yDomain: singleAxis != null && singleAxis !== 'second' && singleAxis !== 'otherSecond' ? yDomain : [yDomain[0], secondAxisValue] as CategoricalDomain | NumberDomain,
    yReverse: yStart < pixelY
  };
};
const getXAndYDomainsAndReverseOnMoveVertical = ({
  xDomain,
  yDomain,
  pixelX,
  pixelY,
  xStart,
  yStart,
  xAxisMinChange,
  firstAxisValue,
  secondAxisValue,
  singleAxis
}: GetXAndYDomainsAndReverseOnMoveProps): GetXAndYDomainsAndReverseOnMoveResult => {
  if (firstAxisValue === yDomain[0]) {
    pixelY = yStart;
  }
  if (secondAxisValue != null && Math.abs(secondAxisValue - (xDomain[0] as number)) < xAxisMinChange) {
    secondAxisValue = xDomain[0] as number;
    pixelX = xStart;
  }
  return {
    xDomain: singleAxis != null && singleAxis !== 'second' ? yDomain : [yDomain[0], secondAxisValue] as CategoricalDomain | NumberDomain,
    xReverse: xStart > pixelX,
    yDomain: singleAxis != null && singleAxis !== 'first' ? xDomain : [xDomain[0], firstAxisValue] as CategoricalDomain | NumberDomain,
    yReverse: yStart < pixelY
  };
};
export const getXAndYDomainsAndReverseOnMove = (props: GetXAndYDomainsAndReverseOnMoveProps): GetXAndYDomainsAndReverseOnMoveResult => {
  if (props.layout === 'horizontal') {
    return getXAndYDomainsAndReverseOnMoveHorizontal(props);
  } else {
    return getXAndYDomainsAndReverseOnMoveVertical(props);
  }
};